/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-underscore-dangle */
import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { useDebounce } from 'react-use';
import classNames from 'classnames';
import {
  connectInfiniteHits,
  connectRefinementList,
  connectStateResults,
} from 'commons/react-instantsearch-dom-wrappers';
import avo from 'analytics/avo';

import useUserCurrentPageType from 'hooks/useUserCurrentPageType';
import ProductListingPage from 'components/consumer/ProductListingPage';
import ProductsGridStatic from 'components/consumer/products-grid/ProductsGridStatic';
import ProductsGridLoading from 'components/consumer/products-grid/components/ProductsGridLoading';
import usePaginatedStaticProductListingPageData from 'hooks/usePaginatedStaticProductListingPageData';
import useProductVariantsToShow from 'hooks/useProductListingData/useProductVariantsToShow';
import useSearchUrlQueryParams from 'components/consumer/consumer-search/advanced-search/hooks/useSearchUrlQueryParams';
import { SORT_BY_OPTIONS } from 'commons/constants';
import SeeFullResultsButton from '../SeeFullResultsButton';

export const productHitsOptionColorMatch = productHits => {
  if (!productHits?.length) {
    return [];
  }

  const firstHit = productHits[0];

  if (firstHit?._highlightResult?.option_color?.length > 0) {
    const { option_color } = firstHit._highlightResult;
    const optionColorEntries = firstHit.option_color || [];

    return option_color.reduce((matched, color, index) => {
      if (color?.matchedWords?.length) {
        if (
          (optionColorEntries[index] || '').toLowerCase().trim() ===
          color.matchedWords[0].toLowerCase().trim()
        ) {
          return [...matched, optionColorEntries[index].toLowerCase().trim()];
        }
      }

      return matched;
    }, []);
  }

  return [];
};

const StaticProductResultsBase = connectStateResults(
  ({
    displayPagesResultComponent,
    forComponent,
    hits: algoliaHits,
    isInstantSearch,
    layout,
    loadingMore,
    items,
    onSortOrderChange,
    onToggleQuickshipFilter,
    optionState,
    pageHeaderOverride,
    resetAlgoliaFilters,
    searching,
    searchResults,
    searchState,
    searchStateQuery,
    setActiveAlgoliaFilter,
    setLoading,
    sidebarHeader,
    sortOrder,
    toggleSearchOpen,
    variant = 'desktop',
    loadRest,
  }) => {
    const fullLoadStatus = useRef('not-initiated'); // not-initiated | initiated | loaded | error

    const searchLoading =
      (isInstantSearch && !searchResults) ||
      (!isInstantSearch && !algoliaHits && !searchResults);

    const needsToLoadMore = !!(
      searchResults?.nbHits &&
      searchResults.nbHits !== searchResults?.hits?.length
    );

    // when its advanced search, swap out the full results when the full results are loadded
    const hits = isInstantSearch
      ? searchResults.hits
      : !needsToLoadMore
      ? searchResults?.hits
      : algoliaHits;

    const gridPlaceholderItemContainerRef = useRef();

    useEffect(() => {
      // conditional display of page results
      if (!hits?.length && isInstantSearch && displayPagesResultComponent) {
        displayPagesResultComponent(true);
      }
      if (isInstantSearch && displayPagesResultComponent) {
        displayPagesResultComponent(false);
      }
    }, [hits, isInstantSearch, displayPagesResultComponent]);

    const colorFacetQuery = productHitsOptionColorMatch(hits);

    const productIDs = useMemo(
      () => hits?.map(hit => parseInt(hit.product_id, 10)) ?? [],
      [hits]
    );

    const facetFilters = searchState?.configure?.facetFilters;

    const productTypeFilterOptions = useMemo(
      () =>
        items
          ?.map(item => item.label)
          ?.filter(Boolean)
          ?.sort()
          .map(label => ({
            id: label.toLowerCase().replace(/\s+/g, '-'),
            label,
            isActive: !!facetFilters?.includes(`type:${label}`),
          })),
      [facetFilters, items]
    );

    const algoliaFilters = useMemo(
      () => [
        {
          id: 'quickship',
          label: null,
          options: null,
        },
        {
          id: 'type',
          label: 'Product Type',
          options: productTypeFilterOptions,
        },
      ],
      [productTypeFilterOptions]
    );

    const [fetchLimit, setFetchLimit] = useState(12);

    const updateFetchLimit = useCallback(() => {
      setFetchLimit(prevLimit => prevLimit + 12);
    }, [setFetchLimit]);

    const isSearchResultsLoading = !searchResults;

    const instantSearchStaticData = usePaginatedStaticProductListingPageData(
      {
        productIds: productIDs,
        showSearchProductsOnly: true,
        algoliaHits: hits,
        algoliaFilters,
        facetFilters,
        isAlgoliaInitialDataLoading: isSearchResultsLoading,
        resetAlgoliaFilters,
        setActiveAlgoliaFilter,
        serverSidePaginationEnabled: true,
        fetchLimit,
      },
      {
        skipSorting: true,
      }
    );

    const productsWithAlgoliaQuickship = useMemo(() => {
      if (!instantSearchStaticData?.products?.length || !hits?.length) {
        return instantSearchStaticData?.products ?? [];
      }

      return hits
        .map(hit => {
          const productId = parseInt(hit.product_id, 10);
          const product = instantSearchStaticData.products.find(
            p => p.id === productId
          );

          if (!product) {
            return null;
          }

          const isInStock = hit?.is_in_stock === true;

          return {
            ...product,
            quickship: isInStock,
            variants: (product.variants || []).map(variantData => ({
              ...variantData,
              quickship: isInStock,
            })),
            algoliaMetaData: {
              queryID: hit?.__queryID ?? searchResults?.queryID ?? '',
              position: hit?.__position ?? 0,
              objectID: hit?.objectID ?? productId ?? '',
            },
          };
        })
        .filter(Boolean);
    }, [instantSearchStaticData?.products, hits, searchResults?.queryID]);

    const productsWithColoredVariants = useProductVariantsToShow({
      colorFilters: colorFacetQuery?.length > 0 ? colorFacetQuery : null,
      products: productsWithAlgoliaQuickship,
    });

    const instantSearchProductsData = useMemo(
      () =>
        colorFacetQuery?.length > 0
          ? productsWithColoredVariants.slice(0, 3)
          : productsWithAlgoliaQuickship?.slice(0, 3) ?? [],
      [
        colorFacetQuery,
        productsWithColoredVariants,
        productsWithAlgoliaQuickship,
      ]
    );

    const { queryUrlParam: query, updateUrlParam } = useSearchUrlQueryParams();

    useDebounce(
      () => {
        if (searchState.query !== query) {
          updateUrlParam({ q: searchState.query });
          setFetchLimit(12);
        }
      },
      1000, // adjusted to resolve query call and re-render race condition
      [searchState.query]
    );

    const currentPage = useUserCurrentPageType();

    const toggleBooleanFilterForSearch = useCallback(
      filterId => {
        if (filterId === 'quickship' && variant === 'advanced-search') {
          const currentFacetFilters =
            searchState?.configure?.facetFilters || '';
          const isQuickshipActive = currentFacetFilters.includes(
            'is_in_stock:true'
          );

          if (onToggleQuickshipFilter) {
            onToggleQuickshipFilter(!isQuickshipActive);
          }
          return;
        }

        if (instantSearchStaticData?.toggleBooleanFilter) {
          instantSearchStaticData.toggleBooleanFilter(filterId);
        }
      },
      [variant, searchState, onToggleQuickshipFilter, instantSearchStaticData]
    );

    const searchPageData = useMemo(() => {
      if (isInstantSearch) {
        return null;
      }

      // Check if quickship filter is active in Algolia facetFilters
      const isQuickshipFilterActive =
        searchState?.configure?.facetFilters?.includes('is_in_stock:true') ||
        false;

      // Update quickship filter isActive state based on Algolia facetFilters
      const filtersWithUpdatedQuickship = (
        instantSearchStaticData?.filters?.filter(
          filterItem => filterItem.id !== 'search'
        ) ?? []
      ).map(filter => {
        if (filter.id === 'quickship') {
          return {
            ...filter,
            isActive: isQuickshipFilterActive,
          };
        }
        return filter;
      });

      // Calculate active filter count from Algolia facetFilters
      const algoliaFacetFilters = searchState?.configure?.facetFilters || [];
      const facetFiltersArray = Array.isArray(algoliaFacetFilters)
        ? algoliaFacetFilters
        : algoliaFacetFilters
        ? [algoliaFacetFilters]
        : [];

      // Count active Algolia filters and add to the existing activeFilterCount
      const algoliaActiveFilterCount = facetFiltersArray.length;
      const totalActiveFilterCount =
        (instantSearchStaticData?.activeFilterCount || 0) +
        algoliaActiveFilterCount;

      const pageData = {
        ...instantSearchStaticData,
        activeFilterCount: totalActiveFilterCount,
        filters: filtersWithUpdatedQuickship,
        pageName: `Results for "${searchStateQuery ?? ''}"`,
        products: productsWithAlgoliaQuickship,
        resetFilters: resetAlgoliaFilters,
        sortOrder,
        setSortOrder: onSortOrderChange,
        // Filter out "New Arrivals" for search page only
        sortByOptions: SORT_BY_OPTIONS.filter(
          option => option.value !== 'newest'
        ),
        toggleBooleanFilter: toggleBooleanFilterForSearch,
        hits,
      };

      if (searchLoading) {
        return {
          ...pageData,
          products:
            pageData?.products?.length === 0
              ? hits?.map(({ product_id, name }) => ({
                  id: product_id,
                  name,
                }))
              : pageData.products,
          showLoadMoreButtonSpinner: pageData?.products?.length !== 0,
          totalProductCount: pageData?.products?.length ?? 0,
        };
      }

      // If we have search results but no hits, show no results state
      if (searchResults && searchResults.nbHits === 0) {
        return {
          ...pageData,
          isFinalProductCountKnown: false,
          totalProductCount: pageData?.products?.length ?? 0,
        };
      }

      const productsWithNames = pageData.products.map(productData => ({
        ...productData,
        name:
          hits.find(
            hit => parseInt(hit.product_id, 10) === parseInt(productData.id, 10)
          )?.name || '',
      }));

      const productsWithAlgoliaMetaData = productsWithNames.map(productData => {
        const matchingHit =
          algoliaHits.find(
            hit => parseInt(hit.product_id, 10) === parseInt(productData.id, 10)
          ) ||
          hits.find(
            hit => parseInt(hit.product_id, 10) === parseInt(productData.id, 10)
          );

        return {
          ...productData,
          algoliaMetaData: {
            queryID: matchingHit?.__queryID ?? searchResults?.queryID ?? '',
            position: matchingHit?.__position ?? 0,
            objectID: matchingHit?.objectID ?? productData?.id ?? '',
          },
        };
      });

      return {
        ...pageData,
        loading: pageData?.loading || isSearchResultsLoading,
        products: productsWithAlgoliaMetaData,
        totalProductCount: searchResults?.nbHits ?? 0,
        loadMoreProducts: updateFetchLimit,
        showLoadMoreButton:
          !isSearchResultsLoading &&
          searchResults?.nbHits &&
          fetchLimit < searchResults?.nbHits,
        showLoadMoreButtonSpinner: loadingMore,
        hideShowAllButton: true,
      };
    }, [
      instantSearchStaticData,
      productsWithAlgoliaQuickship,
      isInstantSearch,
      isSearchResultsLoading,
      searchLoading,
      searchStateQuery,
      searchResults,
      searchState,
      hits,
      sortOrder,
      onSortOrderChange,
      toggleBooleanFilterForSearch,
      fetchLimit,
      updateFetchLimit,
      loadingMore,
      resetAlgoliaFilters,
      algoliaHits,
    ]);

    useEffect(() => {
      if (isInstantSearch) {
        return;
      }

      // If loading of all products has not yet started, load all products when
      // the browser is idle
      if (needsToLoadMore && fullLoadStatus.current === 'not-initiated') {
        fullLoadStatus.current = 'initiated';
        window.requestIdleCallback(() => {
          loadRest();
        });
      } else if (!needsToLoadMore && fullLoadStatus.current === 'initiated') {
        fullLoadStatus.current = 'loaded';
      }
    }, [needsToLoadMore, loadRest, isInstantSearch, fullLoadStatus]);

    return (
      <div
        className={classNames(
          'instant-search-form__product-results flex-1 pt-5 pb-10 [&_.ais-Hits-list]:flex [&_.ais-Hits-item]:p-2.5 [&_.ais-Hits-list]:[list-style:none] max-[1023px]:[&_.ais-SearchBox-input]:pl-4',
          {
            'pr-8': isInstantSearch,
          }
        )}
      >
        {forComponent === 'mobile' && !searchLoading && (
          <div
            className={classNames('mx-0 my-2.5 font-bold text-sm', {
              'hidden lg:block': variant === 'advanced-search',
              'text-gray-light2': variant !== 'mobile',
              'text-brand block mt-[15px] mb-[7px]': variant === 'mobile',
            })}
          >
            Results
          </div>
        )}

        <div
          onClick={() => {
            if (searchState?.query !== '') {
              avo.productsSearched({
                query: searchState.query,
                location: currentPage,
                searchTarget: avo.SearchTarget.PRODUCT_GRID,
              });
            }
          }}
        >
          {variant === 'advanced-search' && (
            <ProductListingPage
              facetFilters={searchState?.configure?.facetFilters}
              pageData={searchPageData}
              pageHeaderOverride={pageHeaderOverride}
              sidebarHeader={sidebarHeader}
              variant="advanced-search"
            />
          )}

          {isInstantSearch &&
            !searchLoading &&
            instantSearchProductsData?.length > 0 && (
              <ProductsGridStatic
                columnsOnDesktop="3"
                columnsOnMobile="2"
                layout={layout || layout}
                listName="Search"
                loading={searching}
                loadingMore={loadingMore}
                maxCardsCount={3}
                optionState={optionState}
                productsData={instantSearchProductsData}
                setLoading={setLoading}
              />
            )}

          {isInstantSearch && searchLoading && (
            <ProductsGridLoading
              cardsCount={3}
              columnsOnDesktop="3"
              columnsOnMobile="2"
              itemContainerRef={gridPlaceholderItemContainerRef}
            />
          )}
        </div>

        {isInstantSearch &&
          !searchLoading &&
          instantSearchStaticData?.products?.length > 0 && (
            <div
              className={classNames('flex mt-5 [&_a]:[text-decoration:none]', {
                'justify-start': variant === 'mobile',
                'justify-center': variant === 'desktop',
              })}
            >
              <SeeFullResultsButton
                toggleSearchOpen={toggleSearchOpen}
                colorFilters={colorFacetQuery}
              />
            </div>
          )}
      </div>
    );
  }
);

StaticProductResultsBase.displayName = 'StaticProductResultsBase';

const StaticProductResultsWithInfiniteHits = connectInfiniteHits(
  StaticProductResultsBase
);

StaticProductResultsWithInfiniteHits.displayName =
  'StaticProductResultsWithInfiniteHits';

const StaticProductResults = connectRefinementList(
  StaticProductResultsWithInfiniteHits
);

StaticProductResults.displayName = 'StaticProductResults';

export default StaticProductResults;
